// The Usage screen's breakdown by one dimension (model, team or user): the rows of its table, one
// line per top entity for its chart (tokens per period — day, ISO week, month or year — the tail
// folded into "Other") and its share-of-tokens pie. Two reads per dimension: `…/usage/by-<dim>`
// and the time series at the chosen grain grouped by it. A model is always shown by its display
// name (primitives/model-names.ts), never by its Bedrock id. Pure apart from the reads;
// tests/unit/usage/usage-model.test.ts.
import type { ApiClient } from "../../api/client";
import { applyMoneyPolicy, type MoneyPolicy } from "../../api/org-context";
import type { components } from "../../api/schema";
import { SERIES_SLOTS } from "../../primitives/chart-model";
import type { LineSeries } from "../../primitives/line-chart";
import { modelDisplayName, modelLabel } from "../../primitives/model-names";
import { formatPercentBp } from "../../primitives/money";
import { MONTH_SHORT_NAMES } from "../../primitives/months";
import type { ChipTone } from "../../primitives/page";
import type { PieSlice } from "../../primitives/pie-chart";
import { classesUnderPolicy } from "./classes-model";
import { classLine, dayLabel, formatCount, totalTokens, type ClassLine } from "./figures";
import { usageDrillPath, type UsageDrill, type UsageHost } from "./hrefs";
import { personName, teamName, UNKNOWN_USER } from "./names";

type ModelUsageItem = components["schemas"]["ModelUsageItem"];
type TeamUsageItem = components["schemas"]["TeamUsageItem"];
type UserUsageItem = components["schemas"]["UserUsageItem"];
type TimeseriesSeries = components["schemas"]["TimeseriesSeries"];
export type ModelFallbackFrom = components["schemas"]["ModelFallbackFrom"];

export type UsageDimension = "model" | "team" | "user";

export const USAGE_DIMENSIONS: readonly { value: UsageDimension; label: string }[] = [
  { value: "model", label: "Model" },
  { value: "team", label: "Team" },
  { value: "user", label: "User" },
];

/** The table's row: one model, team or user over the window. */
export interface EntityUsageRow {
  key: string;
  label: string;
  /** On hover when the label is a short form (a model's full name and id). */
  title?: string;
  /** Under the label when it adds something (a user's email). */
  detail: string | null;
  requests: number;
  tokens: number;
  costMicroUsd: number | null;
  /** The org's price (API-236); null where the server withheld it. */
  priceMicroUsd: number | null;
  /** The models whose usage the money leaves out (REQ-017); empty when it is whole. */
  unpricedModels: string[];
  /** The row's token classes and their money under the policy, for its row detail (SCR-584). */
  classes: ClassLine;
  /** A model row's served fallbacks (SCR-586); null on a team or user row. */
  fallbacks: ModelFallbacks | null;
}

/** The requests a served model answered as a fallback, by the family fallen back from (API-582). */
export interface ModelFallbacks {
  requests: number;
  /** Most requests first, as the API sorts them; the requests sum to `requests`. */
  from: readonly ModelFallbackFrom[];
}

export interface DimensionUsage {
  /** Most tokens first. */
  rows: EntityUsageRow[];
  series: LineSeries[];
}

/** The table shows the top 50 of a dimension (the API's first page, sorted by cost). */
const ROW_LIMIT = 50;
const OTHER = "other";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export async function loadDimensionUsage(
  client: ApiClient,
  orgId: string,
  dimension: UsageDimension,
  window: { from: string; to: string },
  grain: UsageGrain,
  policy: MoneyPolicy,
): Promise<DimensionUsage> {
  const path = { orgId };
  const query = { ...window, limit: ROW_LIMIT };
  const [rows, timeseries] = await Promise.all([
    loadRows(client, path, query, dimension, policy),
    client.get("/v1/orgs/{orgId}/usage/timeseries", { path, query: { ...window, grain, groupBy: dimension } }),
  ]);
  const named = dimension === "model" ? timeseries.series.map(namedModelSeries) : timeseries.series;
  return { rows: rows.sort(byTokens), series: periodLines(named, window.from, window.to, grain) };
}

/** A model series labelled by its display name; the API labels it by its id, which nobody reads. */
function namedModelSeries(series: TimeseriesSeries): TimeseriesSeries {
  return series.key === OTHER ? series : { ...series, label: modelDisplayName(series.key) };
}

function loadRows(client: ApiClient, path: { orgId: string }, query: { from: string; to: string; limit: number }, dimension: UsageDimension, policy: MoneyPolicy) {
  switch (dimension) {
    case "model":
      return client.get("/v1/orgs/{orgId}/usage/by-model", { path, query }).then((page) => page.items.map((item) => modelRow(item, policy)));
    case "team":
      return client.get("/v1/orgs/{orgId}/usage/by-team", { path, query }).then((page) => page.items.map((item) => teamRow(item, policy)));
    case "user":
      return client.get("/v1/orgs/{orgId}/usage/by-user", { path, query }).then((page) => page.items.map((item) => userRow(item, policy)));
  }
}

/** Most tokens first: the API sorts by cost, which a hidden cost cannot show. */
function byTokens(a: { tokens: number; label: string }, b: { tokens: number; label: string }): number {
  return b.tokens - a.tokens || a.label.localeCompare(b.label);
}

function modelRow(item: ModelUsageItem, policy: MoneyPolicy): EntityUsageRow {
  return {
    key: item.bedrockModelId,
    ...modelLabel(item.bedrockModelId),
    detail: null,
    ...measures(item, policy),
    // Required on the wire (A6); a server older than API-582 sends neither: no fallbacks.
    fallbacks: { requests: item.fallbackRequests ?? 0, from: item.fallbacksFrom ?? [] },
  };
}

function teamRow(item: TeamUsageItem, policy: MoneyPolicy): EntityUsageRow {
  return { key: item.teamId ?? "no-team", label: item.teamId === null ? "No team" : teamName(item.teamName), detail: null, ...measures(item, policy), fallbacks: null };
}

/** The display name, else the email, else the external id; the email underneath when it is not the label. */
function userRow(item: UserUsageItem, policy: MoneyPolicy): EntityUsageRow {
  const label = personName({ displayName: item.displayName, email: item.email, externalId: item.userExternalId });
  return { key: item.userId, label, detail: item.email && item.email !== label ? item.email : null, ...measures(item, policy), fallbacks: null };
}

/** A row's figures under the policy, applied once: its money and its class split cannot disagree. */
function measures(item: components["schemas"]["UsageTotals"], policy: MoneyPolicy) {
  const classes = classesUnderPolicy(policy, classLine(item));
  return {
    requests: item.requests,
    tokens: totalTokens(item),
    costMicroUsd: classes.costMicroUsd,
    priceMicroUsd: classes.priceMicroUsd,
    unpricedModels: item.unpricedModels ?? [],
    classes,
  };
}

/** How many families the fallback line names before it says "and N others". */
const NAMED_FAMILIES = 2;

/**
 * The line under a model's name in the by-model table (SCR-586): "38 requests fell back from Opus
 * 5.5", "… from Opus 5.5 and Sonnet 5", or "… from Opus 5.5 and 2 others" past two families, each
 * family by its display name; null when the model served no fallback.
 */
export function fallbackText(fallbacks: ModelFallbacks, names: (family: string) => string = modelDisplayName): string | null {
  if (fallbacks.requests <= 0) return null;
  const count = `${formatCount(fallbacks.requests)} ${fallbacks.requests === 1 ? "request" : "requests"} fell back`;
  const families = fallbacks.from.map((entry) => names(entry.family));
  if (families.length === 0) return count;
  if (families.length <= NAMED_FAMILIES) return `${count} from ${families.join(" and ")}`;
  const others = families.length - 1;
  return `${count} from ${families[0]} and ${others} others`;
}

/** Every family a model fell back from with its requests, for the line's hover and its row detail: "Opus 5.5: 30 · Sonnet 5: 8". */
export function fallbacksTitle(fallbacks: ModelFallbacks, names: (family: string) => string = modelDisplayName): string {
  return fallbacks.from.map((entry) => `${names(entry.family)}: ${formatCount(entry.requests)}`).join(" · ");
}

// ---- grains: the chart's period --------------------------------------------------------------

/** The chart's bucket: the API's `grain` less `hour`, which no Usage-tab chart asks for. */
export type UsageGrain = Exclude<components["schemas"]["TimeseriesGrain"], "hour">;

export const GRAIN_OPTIONS: readonly { value: UsageGrain; label: string }[] = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
];

const DEFAULT_GRAIN: UsageGrain = "day";
const GRAIN_PARAM = "grain";

/** The URL's grain when it is one the charts offer, else daily. */
export function grainFrom(query: URLSearchParams): UsageGrain {
  const asked = query.get(GRAIN_PARAM);
  return GRAIN_OPTIONS.some((option) => option.value === asked) ? (asked as UsageGrain) : DEFAULT_GRAIN;
}

/** The search string with the grain set (or, for the default, left out) and everything else kept. */
export function grainSearch(query: URLSearchParams, grain: UsageGrain): string {
  const next = new URLSearchParams(query);
  if (grain === DEFAULT_GRAIN) next.delete(GRAIN_PARAM);
  else next.set(GRAIN_PARAM, grain);
  return next.toString();
}

/**
 * The window the chart shows at a grain, ending tomorrow (UTC, `to` exclusive) so today is in:
 * 30 days, 13 weeks from a Monday, 13 months or three whole calendar years — each within the
 * API's cap for the grain (31 days of hours never asked for; 36 months for the folded grains).
 */
export function grainWindow(now: Date, grain: UsageGrain): { from: string; to: string } {
  const tomorrow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
  const today = tomorrow - ONE_DAY_MS;
  const from = {
    day: tomorrow - 30 * ONE_DAY_MS,
    week: periodStartMs(today - 13 * 7 * ONE_DAY_MS, "week"),
    month: Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 12, 1),
    year: Date.UTC(now.getUTCFullYear() - 2, 0, 1),
  }[grain];
  return { from: isoDay(from), to: isoDay(tomorrow) };
}

/**
 * One line per series, tokens per period of the window (a period without a bucket is zero). The API
 * sends the top 20 plus "other"; past seven named lines the smaller ones fold into "Other" too, so
 * no line ever gets a cycled colour. Biggest first, "Other" last.
 */
export function periodLines(series: readonly TimeseriesSeries[], from: string, to: string, grain: UsageGrain): LineSeries[] {
  const periods = periodStarts(from, to, grain);
  const lines = series.map((s) => periodLine(s, periods));
  const named = lines.filter((line) => line.id !== OTHER).sort((a, b) => lineTotal(b) - lineTotal(a) || a.label.localeCompare(b.label));
  const tail = [...named.slice(SERIES_SLOTS - 1), ...lines.filter((line) => line.id === OTHER)];
  const kept = named.slice(0, SERIES_SLOTS - 1);
  return tail.length > 0 ? [...kept, sumLines(tail, periods)] : kept;
}

function periodLine(series: TimeseriesSeries, periods: readonly string[]): LineSeries {
  const byPeriod = new Map(series.points.map((point) => [point.bucket.slice(0, 10), totalTokens(point)]));
  return { id: series.key, label: series.label, points: periods.map((period) => ({ x: period, y: byPeriod.get(period) ?? 0 })) };
}

function lineTotal(line: LineSeries): number {
  return line.points.reduce((sum, point) => sum + point.y, 0);
}

function sumLines(lines: readonly LineSeries[], periods: readonly string[]): LineSeries {
  return { id: OTHER, label: "Other", points: periods.map((period, i) => ({ x: period, y: lines.reduce((sum, line) => sum + (line.points[i]?.y ?? 0), 0) })) };
}

/** At most this many points on a line, whatever the window asks. */
const MAX_PERIODS = 400;

/**
 * The `YYYY-MM-DD` starts of the periods a window touches at a grain, as the API buckets them: each
 * UTC day of [from, to); each ISO week (from its Monday), month or year that holds a day of it.
 */
export function periodStarts(from: string, to: string, grain: UsageGrain): string[] {
  const end = Date.parse(`${to}T00:00:00Z`);
  const starts: string[] = [];
  for (let time = periodStartMs(Date.parse(`${from}T00:00:00Z`), grain); time < end && starts.length < MAX_PERIODS; time = nextPeriodMs(time, grain)) {
    starts.push(isoDay(time));
  }
  return starts;
}

/** The UTC start of the period holding `ms` at a grain, as a timestamp. */
function periodStartMs(ms: number, grain: UsageGrain): number {
  const d = new Date(ms);
  switch (grain) {
    case "day":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    case "week":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
    case "month":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
    case "year":
      return Date.UTC(d.getUTCFullYear(), 0, 1);
  }
}

function nextPeriodMs(start: number, grain: UsageGrain): number {
  const d = new Date(start);
  switch (grain) {
    case "day":
      return start + ONE_DAY_MS;
    case "week":
      return start + 7 * ONE_DAY_MS;
    case "month":
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    case "year":
      return Date.UTC(d.getUTCFullYear() + 1, 0, 1);
  }
}

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** The period in full, for the readout and the table: "4 Mar 2026", "Week 10, 2026 (from 2 Mar)", "Mar 2026", "2026". */
export function formatPeriod(start: string, grain: UsageGrain): string {
  const [year, month] = start.split("-");
  switch (grain) {
    case "day":
      return dayLabel(start);
    case "week": {
      const { week, year: isoYear } = isoWeek(start);
      return `Week ${week}, ${isoYear} (from ${dayLabel(start).slice(0, -5)})`;
    }
    case "month":
      return `${monthName(month)} ${year}`;
    case "year":
      return year ?? start;
  }
}

/** The short tick under the axis: "4", "W10", "Mar" (with the year in January), "2026". */
export function formatPeriodTick(start: string, grain: UsageGrain): string {
  const [year, month] = start.split("-");
  switch (grain) {
    case "day":
      return String(Number(start.slice(8, 10)));
    case "week":
      return `W${isoWeek(start).week}`;
    case "month":
      return month === "01" ? `${monthName(month)} ${year}` : monthName(month);
    case "year":
      return year ?? start;
  }
}

function monthName(month: string | undefined): string {
  return MONTH_SHORT_NAMES[Number(month) - 1] ?? month ?? "";
}

/** The ISO-8601 week number and week-based year of a `YYYY-MM-DD` day (weeks start on Monday). */
function isoWeek(day: string): { week: number; year: number } {
  const date = new Date(Date.parse(`${day}T00:00:00Z`));
  // Move to the Thursday of this week: its calendar year is the ISO week-based year.
  const thursday = new Date(date.getTime() + (3 - ((date.getUTCDay() + 6) % 7)) * ONE_DAY_MS);
  const year = thursday.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4));
  const week1Monday = Date.UTC(year, 0, 4 - ((firstThursday.getUTCDay() + 6) % 7));
  return { week: Math.round((thursday.getTime() - week1Monday) / (7 * ONE_DAY_MS)) + 1, year };
}

/**
 * The share pie's slices: tokens per entity of the chosen dimension (the table's rows, named as the
 * table names them), biggest first, past seven one "Other".
 */
export function shareSlices(rows: readonly Pick<EntityUsageRow, "key" | "label" | "tokens">[]): PieSlice[] {
  const sorted = rows.filter((row) => row.tokens > 0).sort(byTokens);
  const kept = sorted.slice(0, SERIES_SLOTS - 1).map((row) => ({ id: row.key, label: row.label, value: row.tokens }));
  const tail = sorted.slice(SERIES_SLOTS - 1).reduce((sum, row) => sum + row.tokens, 0);
  return tail > 0 ? [...kept, { id: OTHER, label: "Other", value: tail }] : kept;
}

// ---------------------------------------------------------------------------------------------
// The usage explorer (TASK-252, SCR-232, REQ-089–091): the org's teams, a team's users, a user's
// models, one level per route (hrefs.ts names the routes in each shell). Its rows come from API-236 (`exploreUsage`) ranked by the API's
// `sort` with the previous equal-length period alongside; a user's models from API-013's by-model
// filtered to that user. Money only as the policy allows (applyMoneyPolicy): a figure the policy
// hides has no column, a null figure in a shown column renders "—". The CSV link is the API's own
// `format=csv` of the same query, so the export carries the server's columns and visibility.

type ExploreItem = components["schemas"]["ExploreRow"];
type ExploreStanding = components["schemas"]["ExploreStanding"];
type QuotaState = components["schemas"]["QuotaState"];
export type UsageSort = components["schemas"]["UsageSort"];
type Totals = components["schemas"]["UsageTotals"];

/** The explorer's filters as the URL holds them: `YYYY-MM-DD` days, both inclusive, and blanks for "any". */
export interface ExplorerFilters {
  from: string;
  to: string;
  model: string;
  platform: string;
}

/** The coding tools the usage-event contract knows (`platform` is an open value). */
export const KNOWN_PLATFORMS: readonly string[] = ["claude-code", "claude-desktop", "codex", "codex-desktop", "cursor", "web", "other"];

const DAY_PATTERN = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

function shiftDay(day: string, days: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + days * ONE_DAY_MS).toISOString().slice(0, 10);
}

/** The filters in the URL, or the tab's 30 days ending today when the range is absent or malformed. */
export function explorerFiltersFrom(query: URLSearchParams, defaultWindow: { from: string; to: string }): ExplorerFilters {
  const day = (name: string) => {
    const value = query.get(name) ?? "";
    return DAY_PATTERN.test(value) ? value : null;
  };
  const from = day("from");
  const to = day("to");
  const ranged = from !== null && to !== null && from <= to;
  return {
    from: ranged ? from : defaultWindow.from,
    to: ranged ? to : shiftDay(defaultWindow.to, -1),
    model: (query.get("model") ?? "").trim(),
    platform: (query.get("platform") ?? "").trim(),
  };
}

/** The draft's field errors: the two days well formed and in order. */
export function explorerFilterErrors(filters: ExplorerFilters): { from?: string; to?: string } {
  if (!DAY_PATTERN.test(filters.from)) return { from: "A date, YYYY-MM-DD." };
  if (!DAY_PATTERN.test(filters.to)) return { to: "A date, YYYY-MM-DD." };
  return filters.from > filters.to ? { to: "Not before the start." } : {};
}

/** The URL's search string for the filters and sort; blanks are left out. */
export function explorerSearch(filters: ExplorerFilters, sort: UsageSort): string {
  const search = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.model !== "") search.set("model", filters.model);
  if (filters.platform !== "") search.set("platform", filters.platform);
  search.set("sort", sort);
  return search.toString();
}

const SORT_LABELS: Readonly<Record<UsageSort, string>> = { spend: "AWS list cost", price: "Price", tokens: "Tokens", requests: "Requests" };

/** The measures rows can rank by: a money measure only when the policy shows that figure. */
export function sortOptions(policy: MoneyPolicy): { value: UsageSort; label: string }[] {
  const allowed: UsageSort[] = [...(policy.list ? (["spend"] as const) : []), ...(policy.price ? (["price"] as const) : []), "tokens", "requests"];
  return allowed.map((value) => ({ value, label: SORT_LABELS[value] }));
}

/** The URL's sort when the policy allows it, else the first measure it does. */
export function sortFrom(query: URLSearchParams, policy: MoneyPolicy): UsageSort {
  const options = sortOptions(policy).map((option) => option.value);
  const asked = query.get("sort") as UsageSort | null;
  return asked !== null && options.includes(asked) ? asked : (options[0] as UsageSort);
}

/** The money columns the policy leaves on screen, in order. */
export function moneyColumns(policy: MoneyPolicy): ("cost" | "price")[] {
  return [...(policy.list ? (["cost"] as const) : []), ...(policy.price ? (["price"] as const) : [])];
}

interface CommonQuery {
  from: string;
  to: string;
  model?: string;
  platform?: string;
  sort: UsageSort;
}

/** The filters as the API's query: `to` made exclusive, blanks left out. */
function commonQuery(filters: ExplorerFilters, sort: UsageSort): CommonQuery {
  const query: CommonQuery = { from: filters.from, to: shiftDay(filters.to, 1), sort };
  if (filters.model !== "") query.model = filters.model;
  if (filters.platform !== "") query.platform = filters.platform;
  return query;
}

/** API-236's query for a level: teams of the org, users of a team, or the one user within it. */
export function exploreQuery(drill: UsageDrill, filters: ExplorerFilters, sort: UsageSort) {
  const base = { ...commonQuery(filters, sort), compare: "previous" as const, limit: EXPLORER_LIMIT };
  if (drill.level === "org") return { ...base, level: "team" as const };
  if (drill.level === "team") return { ...base, level: "user" as const, teamId: drill.teamId };
  return { ...base, level: "user" as const, teamId: drill.teamId, userId: drill.userId };
}

/** API-013's by-model query for one user's models. */
export function userModelsQuery(drill: Extract<UsageDrill, { level: "user" }>, filters: ExplorerFilters, sort: UsageSort) {
  return { ...commonQuery(filters, sort), teamId: drill.teamId, userId: drill.userId, limit: EXPLORER_LIMIT };
}

/** The explorer shows the top 100 at a level (the API's `limit`; it does not page). */
export const EXPLORER_LIMIT = 100;

/** A row of the explorer's table. Money is already under the policy; null renders "—". */
export interface ExplorerRow {
  key: string;
  label: string;
  /** On hover when the label is a short form (a model's full name and id). */
  title?: string;
  detail: string | null;
  /** The level below, when there is one to open. */
  href: string | null;
  requests: number;
  tokens: number;
  costMicroUsd: number | null;
  priceMicroUsd: number | null;
  /** The models whose usage both money figures leave out (REQ-017); empty when they are whole. */
  unpricedModels: string[];
  /** The row's token classes and their money under the policy, for its row detail (SCR-584). */
  classes: ClassLine;
  shareBp: number | null;
  /** Change of the ranked measure against the previous period, in basis points; "new" from nothing. */
  delta: number | "new" | null;
  standing: StandingBadge | null;
}

/** The ranked measure of a set of totals, after the policy; null when it is money not shown. */
export function sortMeasure(totals: Totals, sort: UsageSort, policy: MoneyPolicy): number | null {
  const shown = applyMoneyPolicy(policy, { costMicroUsd: totals.costMicroUsd, priceMicroUsd: totals.priceMicroUsd });
  switch (sort) {
    case "spend":
      return shown.costMicroUsd;
    case "price":
      return shown.priceMicroUsd;
    case "tokens":
      return totalTokens(totals);
    case "requests":
      return totals.requests;
  }
}

/** Basis points of change from `previous` to `current`; "new" from zero; null when either is unknown or both are zero. */
export function deltaBp(current: number | null, previous: number | null): number | "new" | null {
  if (current === null || previous === null) return null;
  if (previous === 0) return current === 0 ? null : "new";
  return Math.round(((current - previous) / previous) * 10_000);
}

export interface StandingBadge {
  tone: ChipTone;
  label: string;
  title: string;
}

const STANDING_TONES: Readonly<Record<QuotaState, ChipTone>> = { ok: "green", warning: "amber", grace: "amber", exhausted: "red", exempt: "blue" };
const STANDING_WORDS: Readonly<Record<QuotaState, string>> = { ok: "OK", warning: "Near limit", grace: "In grace", exhausted: "Exhausted", exempt: "Exempt" };

/** The tightest quota's badge: its state and how much of it is used; the quota named on hover. */
export function standingBadge(standing: ExploreStanding): StandingBadge {
  const used = standing.state === "exempt" ? "" : ` · ${formatPercentBp(standing.utilizationBp, 0)}`;
  return {
    tone: STANDING_TONES[standing.state],
    label: `${STANDING_WORDS[standing.state]}${used}`,
    title: `${standing.quotaName}: ${standing.metric} per ${standing.period}, resets ${standing.resetsAt.slice(0, 10)}`,
  };
}

export const NO_TEAM_KEY = "no-team";

function exploreLabel(item: ExploreItem): { key: string; label: string; detail: string | null } {
  if (item.level === "user") {
    const label = personName({ displayName: item.displayName, email: item.email, externalId: item.userExternalId });
    return { key: item.userId ?? label, label, detail: item.email && item.email !== label ? item.email : null };
  }
  if (item.teamId === null || item.teamId === undefined) return { key: NO_TEAM_KEY, label: "No team", detail: null };
  return { key: item.teamId, label: teamName(item.teamName), detail: null };
}

/**
 * The level below a row: a team opens its users, a user its models. Users without a team have no
 * team to filter by (API-236's `teamId` names one team), so the "No team" row opens nothing.
 */
function childDrill(item: ExploreItem, drill: UsageDrill): UsageDrill | null {
  if (item.level === "team") return item.teamId ? { level: "team", teamId: item.teamId } : null;
  if (item.level === "user" && item.userId && drill.level !== "org") return { level: "user", teamId: drill.teamId, userId: item.userId };
  return null;
}

/** What shapes an explorer row besides the item: where the explorer is, how it ranks, what money it shows, and the shell its links live in. */
export interface ExplorerRowContext {
  host: UsageHost;
  orgId: string;
  drill: UsageDrill;
  sort: UsageSort;
  policy: MoneyPolicy;
  search: string;
}

/** One explorer row under the policy, its delta on the ranked measure, its link one level down. */
export function explorerRow(item: ExploreItem, context: ExplorerRowContext): ExplorerRow {
  const { policy, sort } = context;
  const child = childDrill(item, context.drill);
  const moneySort = sort === "spend" || sort === "price";
  return {
    ...exploreLabel(item),
    href: child ? usageDrillPath(context.host, context.orgId, child, context.search) : null,
    ...measures(item, policy),
    shareBp: moneySort && sortMeasure(item, sort, policy) === null ? null : item.shareBp,
    delta: deltaBp(sortMeasure(item, sort, policy), item.previous ? sortMeasure(item.previous, sort, policy) : null),
    standing: item.standing ? standingBadge(item.standing) : null,
  };
}

/** A model row of one user's breakdown: no share, delta or standing from API-013. */
function userModelRow(item: ModelUsageItem, policy: MoneyPolicy): ExplorerRow {
  return {
    key: item.bedrockModelId,
    ...modelLabel(item.bedrockModelId),
    detail: null,
    href: null,
    ...measures(item, policy),
    shareBp: null,
    delta: null,
    standing: null,
  };
}

export interface ExplorerCrumb {
  label: string;
  href: string | null;
}

export interface ExplorerView {
  /** Org → team → user; the last is where the explorer is. */
  crumbs: ExplorerCrumb[];
  /** What the rows are ("Teams", "Users of Platform", "Models used by Ana"). */
  noun: "team" | "user" | "model";
  rows: ExplorerRow[];
  /** The user's own row at the user level (share of the team, delta, standing). */
  subject: ExplorerRow | null;
  /** The previous period the deltas compare with, when the API compared. */
  comparedWith: { from: string; to: string } | null;
  csvHref: string;
}

export interface ExplorerRequest {
  host: UsageHost;
  orgId: string;
  drill: UsageDrill;
  filters: ExplorerFilters;
  sort: UsageSort;
  policy: MoneyPolicy;
}

const ALL_TEAMS = "All teams";

/** Reads one level of the explorer and shapes it for the table, the breadcrumb and the CSV link. */
export async function loadExplorer(client: ApiClient, request: ExplorerRequest): Promise<ExplorerView> {
  const { host, orgId, drill, filters, sort, policy } = request;
  const path = { orgId };
  const search = explorerSearch(filters, sort);
  const query = exploreQuery(drill, filters, sort);
  const answer = await client.get("/v1/orgs/{orgId}/usage/explore", { path, query });
  const rowContext: ExplorerRowContext = { host, orgId, drill, sort, policy, search };
  const rows = answer.items.map((item) => explorerRow(item, rowContext));
  const comparedWith = answer.comparedWith ? { from: answer.comparedWith.from, to: answer.comparedWith.to } : null;
  const orgCrumb: ExplorerCrumb = { label: ALL_TEAMS, href: drill.level === "org" ? null : usageDrillPath(host, orgId, { level: "org" }, search) };

  if (drill.level === "org") {
    return { crumbs: [orgCrumb], noun: "team", rows, subject: null, comparedWith, csvHref: client.csvUrl("/v1/orgs/{orgId}/usage/explore", { path, query }) };
  }
  if (drill.level === "team") {
    const crumbs = [orgCrumb, { label: answer.parent.name, href: null }];
    return { crumbs, noun: "user", rows, subject: null, comparedWith, csvHref: client.csvUrl("/v1/orgs/{orgId}/usage/explore", { path, query }) };
  }
  const modelsQuery = userModelsQuery(drill, filters, sort);
  const models = await client.get("/v1/orgs/{orgId}/usage/by-model", { path, query: modelsQuery });
  const subject = rows[0] ?? null;
  const crumbs = [
    orgCrumb,
    { label: answer.parent.name, href: usageDrillPath(host, orgId, { level: "team", teamId: drill.teamId }, search) },
    { label: subject?.label ?? UNKNOWN_USER, href: null },
  ];
  return {
    crumbs,
    noun: "model",
    rows: models.items.map((item) => userModelRow(item, policy)),
    subject: subject && { ...subject, href: null },
    comparedWith,
    csvHref: client.csvUrl("/v1/orgs/{orgId}/usage/by-model", { path, query: modelsQuery }),
  };
}

/** The explorer endpoint is a placeholder until its handler ships: 501 reads as "not available yet". */
export function explorerNotAvailable(error: { status: number }): boolean {
  return error.status === 501;
}

/** "+12.5%", "−3.0%", "New", or "—" for a delta. */
export function formatDelta(delta: number | "new" | null): string {
  if (delta === null) return "—";
  if (delta === "new") return "New";
  return delta > 0 ? `+${formatPercentBp(delta)}` : formatPercentBp(delta);
}

/** The previous period as inclusive UTC days ("2026-08-02 – 2026-08-31"); the API's `to` is exclusive. */
export function comparedDays(period: { from: string; to: string }): { first: string; last: string } {
  return { first: period.from.slice(0, 10), last: shiftDay(period.to.slice(0, 10), -1) };
}
